/**
 * Yes/No confirmation gate.
 *
 * Every data-changing action in the app asks before it fires. Rather than
 * repeat a `Modal.confirm` block at each of the ~110 call sites, actions are
 * gated once where the mutation is DEFINED — `useConfirmedMutation` returns a
 * normal react-query mutation whose `mutate`/`mutateAsync` first raise the
 * dialog, so every call site of that mutation is covered for free.
 *
 * The dialog awaits the mutation (via `mutateAsync`), so the Yes button spins
 * while the request is in flight and the dialog stays open if it fails — the
 * same behaviour the hand-written `Modal.confirm` blocks already had.
 */
import { useMemo, useRef, type ReactNode } from 'react';
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  type MutateOptions,
} from '@tanstack/react-query';
import { Modal } from '../ui';

export interface ConfirmOpts {
  /** The question. Phrase it as one — "Delete this banner?", not "Delete banner". */
  title: ReactNode;
  /** What actually happens, and whether it can be undone. Skip when it adds nothing. */
  content?: ReactNode;
  okText?: ReactNode;
  cancelText?: ReactNode;
  /** Red confirm button — for anything destructive or irreversible. */
  danger?: boolean;
}

/** Raised on the caller's promise when the user answers No. */
export class ConfirmCancelled extends Error {
  constructor() {
    super('cancelled');
    this.name = 'ConfirmCancelled';
  }
}

export const isConfirmCancelled = (e: unknown): boolean =>
  e instanceof ConfirmCancelled || (e as any)?.name === 'ConfirmCancelled';

/**
 * Ask, then run `onYes`. The dialog holds its spinner until `onYes` settles.
 * Use this for imperative handlers that don't own a mutation of their own.
 */
export function confirmAction(opts: ConfirmOpts, onYes: () => void | Promise<unknown>): void {
  Modal.confirm({
    title: opts.title,
    content: opts.content,
    okText: opts.okText ?? 'Yes',
    cancelText: opts.cancelText ?? 'No',
    okButtonProps: { danger: !!opts.danger },
    onOk: onYes,
  });
}

/** Curried `confirmAction`, for dropping straight into an `onClick`. */
export const confirmClick =
  (opts: ConfirmOpts, onYes: () => void | Promise<unknown>) =>
  (e?: { stopPropagation?: () => void }): void => {
    e?.stopPropagation?.();
    confirmAction(opts, onYes);
  };

type ConfirmFor<TVars> = ConfirmOpts | ((vars: TVars) => ConfirmOpts);

/**
 * `useMutation` that asks Yes/No first.
 *
 * `confirm` may be a function of the variables, so the question can name the
 * record it is about ("Delete Rajesh Traders?" beats "Delete this row?").
 *
 * Answering No is not a failure: `mutate` simply does nothing, and the
 * mutation never leaves idle, so no `onError` toast fires. `mutateAsync`
 * rejects with `ConfirmCancelled` — test it with `isConfirmCancelled`.
 */
export function useConfirmedMutation<TData = unknown, TError = unknown, TVars = void, TCtx = unknown>(
  confirm: ConfirmFor<TVars>,
  options: UseMutationOptions<TData, TError, TVars, TCtx>,
): UseMutationResult<TData, TError, TVars, TCtx> {
  const m = useMutation<TData, TError, TVars, TCtx>(options);

  // Held in a ref so an inline `{ title: ... }` literal — a new object every
  // render — does not churn the identity of the returned `mutate`.
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;

  return useMemo(() => {
    const optsFor = (vars: TVars): ConfirmOpts => {
      const c = confirmRef.current;
      return typeof c === 'function' ? (c as (v: TVars) => ConfirmOpts)(vars) : c;
    };

    const mutateAsync = (vars: TVars, callOpts?: MutateOptions<TData, TError, TVars, TCtx>) =>
      new Promise<TData>((resolve, reject) => {
        const o = optsFor(vars);
        Modal.confirm({
          title: o.title,
          content: o.content,
          okText: o.okText ?? 'Yes',
          cancelText: o.cancelText ?? 'No',
          okButtonProps: { danger: !!o.danger },
          // Resolve/reject only after the request settles, so the dialog holds
          // its spinner and stays open on failure.
          onOk: () => m.mutateAsync(vars, callOpts).then(resolve, (err) => { reject(err); throw err; }),
          onCancel: () => reject(new ConfirmCancelled()),
        });
      });

    return {
      ...m,
      mutate: (vars: TVars, callOpts?: MutateOptions<TData, TError, TVars, TCtx>) => {
        // Swallow both the "No" rejection and the request failure: the
        // mutation's own onError already reports the latter.
        void mutateAsync(vars, callOpts).catch(() => {});
      },
      mutateAsync,
    } as UseMutationResult<TData, TError, TVars, TCtx>;
  }, [m]);
}
